import { Link } from 'react-router-dom'
import { TrendingUp, GraduationCap, Zap, Users, Cog, AlertTriangle, LineChart, ArrowRight } from 'lucide-react'
import { AI_WAGE_PREMIUM_BASELINE } from '../utils/constants.ts'

const calculators = [
  {
    path: '/ai-salary-gap',
    title: 'AI Salary Gap',
    description: 'Quantify your personal AI wage premium. Compare your salary path against an AI-upskilled peer in your exact role.',
    icon: TrendingUp,
    color: 'bg-primary-50 text-primary-600',
    cta: 'Find your gap'
  },
  {
    path: '/skills-roi',
    title: 'AI Skills ROI',
    description: 'Calculate the return on learning any AI skill. Time, cost, payback period, and lifetime premium.',
    icon: GraduationCap,
    color: 'bg-emerald-50 text-emerald-600',
    cta: 'Pick a skill'
  },
  {
    path: '/ai-productivity',
    title: 'AI Productivity Value',
    description: 'Turn hours saved into dollars. See how AI tools add up to FTE-equivalent productivity per year.',
    icon: Zap,
    color: 'bg-amber-50 text-amber-700',
    cta: 'Value your time'
  },
  {
    path: '/hire-vs-ai',
    title: 'Hire vs. AI',
    description: 'Run the math for business owners: hire a person, or automate with AI tools?',
    icon: Users,
    color: 'bg-violet-50 text-violet-600',
    cta: 'Compare options'
  },
  {
    path: '/automation-savings',
    title: 'Automation Savings',
    description: 'Estimate labor hours saved, error reduction, and payback period for automating a process.',
    icon: Cog,
    color: 'bg-cyan-50 text-cyan-600',
    cta: 'Estimate savings'
  },
  {
    path: '/displacement-risk',
    title: 'AI Displacement Risk',
    description: 'Reframe risk in dollars: income at risk, wage compression, and years until disruption.',
    icon: AlertTriangle,
    color: 'bg-rose-50 text-rose-600',
    cta: 'Check your risk'
  },
  {
    path: '/lifetime-earnings',
    title: 'Lifetime Earnings',
    description: 'Model your entire career trajectory with and without AI upskilling. NPV and breakeven age.',
    icon: LineChart,
    color: 'bg-indigo-50 text-indigo-600',
    cta: 'Model your career'
  }
]

export default function HomePage() {
  return (
    <div>
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Turn the AI wage premium into your personal dollar figure.
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8">
              Workers with AI skills now earn a {AI_WAGE_PREMIUM_BASELINE}% wage premium over peers in identical roles. Use our 48 calculators to quantify your gap, close it, and future-proof your income - whether you're an employee, founder, consultant, or HR leader.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/ai-salary-gap" className="btn-primary">
                Start with AI Salary Gap <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/displacement-risk" className="btn-secondary">
                Check displacement risk
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">48 calculators, one income decision</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Each tool is deterministic, data-backed, and built around the same insight: AI skills are no longer optional for income growth.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {calculators.map((calc) => {
              const Icon = calc.icon
              return (
                <Link
                  key={calc.path}
                  to={calc.path}
                  className="card group hover:shadow-md transition-shadow p-6 flex flex-col"
                >
                  <div className={`w-12 h-12 rounded-lg ${calc.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-primary-700 transition-colors">
                    {calc.title}
                  </h3>
                  <p className="text-slate-600 mb-6 flex-1">{calc.description}</p>
                  <div className="flex items-center text-primary-600 font-medium">
                    {calc.cta} <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">{AI_WAGE_PREMIUM_BASELINE}%</div>
              <div className="text-slate-900 font-medium">AI wage premium</div>
              <div className="text-sm text-slate-500">PwC 2025 Global AI Jobs Barometer</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">48</div>
              <div className="text-slate-900 font-medium">Decision calculators</div>
              <div className="text-sm text-slate-500">From salary gap to infrastructure, agents, and career transitions</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600 mb-2">120+</div>
              <div className="text-slate-900 font-medium">Programmatic landing pages</div>
              <div className="text-sm text-slate-500">Role, industry, and skill combinations</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
